// Pure helpers for the Relay Voice agent phone: what was typed, what may be
// dialled, and how numbers and times are shown. The switch enforces the real
// rules; these only give the agent an early, honest answer.

export const KEYPAD_KEYS = [
  { key: '1', letters: '' },
  { key: '2', letters: 'ABC' },
  { key: '3', letters: 'DEF' },
  { key: '4', letters: 'GHI' },
  { key: '5', letters: 'JKL' },
  { key: '6', letters: 'MNO' },
  { key: '7', letters: 'PQRS' },
  { key: '8', letters: 'TUV' },
  { key: '9', letters: 'WXYZ' },
  { key: '*', letters: '' },
  { key: '0', letters: '+' },
  { key: '#', letters: '' },
];

export const ECHO_TEST = '9196';
export const MOBILE_PREFIXES = ['70', '80', '81', '90', '91'];
const MAX_INPUT = 20;

export const isKeypadKey = value => /^[0-9*#]$/.test(value);

// Keeps what a person could mean by a phone number: digits, a leading +,
// * and #. Spaces, brackets, dots and dashes from a pasted number go.
export const cleanDialInput = raw => {
  const text = String(raw ?? '').trim();
  const plus = text.startsWith('+') ? '+' : '';
  return (plus + text.replace(/[^0-9*#]/g, '')).slice(0, MAX_INPUT);
};

// A Nigerian national number (8 digits for a landline, 10 for a mobile) from
// 0803..., 01 234 5678, 234... or +234...; null for anything else.
const nigerianNational = value => {
  if (/^0[1-9]\d{9}$/.test(value) || /^0[1-9]\d{7}$/.test(value))
    return value.slice(1);
  const match = /^(?:\+|00)?234([1-9](?:\d{7}|\d{9}))$/.exec(value);
  return match ? match[1] : null;
};

// An international number written with + or 00 (not Nigeria): E.164 digits, or null.
const internationalDigits = value => {
  const match = /^(?:\+|00)([1-9]\d{7,14})$/.exec(value);
  return match && !match[1].startsWith('234') ? match[1] : null;
};

// What the typed value would dial:
// { kind: 'echo' | 'extension' | 'phone' | 'invalid' | 'empty', target, reason, international }
// scope is the Voice /me outbound_scope: 'nigeria_mobile' allows Nigerian
// mobiles only, 'international' any E.164 number (landlines too), anything
// else Nigerian numbers with the switch deciding the prefixes. blocked are the
// team lead's premium-rate prefixes (E.164 without +) while the blocklist is on.
export const parseDestination = (
  raw,
  { scope = 'nigeria_mobile', outbound = true, blocked = [] } = {}
) => {
  const value = cleanDialInput(raw);
  if (!value) return { kind: 'empty', target: '' };
  if (value === ECHO_TEST) return { kind: 'echo', target: ECHO_TEST };
  if (/^1\d{3}$/.test(value)) return { kind: 'extension', target: value };
  const national = nigerianNational(value);
  const foreign = national ? null : internationalDigits(value);
  if (!national && !foreign)
    return { kind: 'invalid', target: '', reason: 'format' };
  if (!outbound) return { kind: 'invalid', target: '', reason: 'not_allowed' };
  if (foreign && scope !== 'international')
    return { kind: 'invalid', target: '', reason: 'not_international' };
  if (
    national &&
    scope === 'nigeria_mobile' &&
    (national.length !== 10 || !MOBILE_PREFIXES.includes(national.slice(0, 2)))
  ) {
    return { kind: 'invalid', target: '', reason: 'not_mobile' };
  }
  const digits = foreign || `234${national}`;
  if (blocked.some(prefix => digits.startsWith(prefix)))
    return { kind: 'invalid', target: '', reason: 'blocked' };
  return foreign
    ? { kind: 'phone', target: `+${digits}`, international: true }
    : { kind: 'phone', target: `+${digits}` };
};

// "+234 803 123 4567" for a Nigerian mobile, "+234 1 234 5678" for a
// landline; anything else as given.
export const formatPhone = raw => {
  const value = String(raw ?? '');
  const digits = value.replace(/\D/g, '');
  let national = null;
  if (/^234\d{10}$/.test(digits) || /^234[1-9]\d{7}$/.test(digits))
    national = digits.slice(3);
  else if (/^0\d{10}$/.test(digits)) national = digits.slice(1);
  if (!national) return value;
  if (national.length === 8)
    return `+234 ${national.slice(0, 1)} ${national.slice(1, 4)} ${national.slice(4)}`;
  return `+234 ${national.slice(0, 3)} ${national.slice(3, 6)} ${national.slice(6)}`;
};

// "0:07", "2:14", "1:02:09"
export const formatDuration = totalSeconds => {
  const seconds = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = String(seconds % 60).padStart(2, '0');
  return h ? `${h}:${String(m).padStart(2, '0')}:${s}` : `${m}:${s}`;
};

// The SIP user part of a remote identity, for an incoming caller.
export const callerNumber = session => session?.remoteIdentity?.uri?.user || '';
